import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  RefreshCw,
  ShieldCheck,
  UserCheck,
  XCircle,
} from 'lucide-react'
import clsx from 'clsx'
import { dbConsoleService, formatBytes, type ExportRequest } from '@/services/dbConsole'
import { downloadBlob } from '@/services/dbSearch'
import { count } from './format'
import { REQUEST_POLL_MS, decisionLine } from './exportStatus'
import { ApproverPicker, AttachmentsOption } from './ApproverPicker'

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-amber-500/10 text-amber-300 ring-amber-500/25',
  approved: 'bg-sky-500/10 text-sky-300 ring-sky-500/25',
  completed: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/25',
  denied: 'bg-red-500/10 text-red-300 ring-red-500/25',
  cancelled: 'bg-white/[0.06] text-stone-400 ring-white/10',
}

function when(iso: string | null): string {
  if (!iso) return '—'
  const parsed = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString()
}

function errorDetail(error: unknown): string | undefined {
  return (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
}

/**
 * Dual-authorization database export.
 *
 * One person requests with a stated reason; a *different* person with approval
 * authority signs off; approval mints a single-use token that expires. The
 * delivered archive carries a manifest with per-file SHA-256 checksums and the
 * row count found in the database at export time, so the recipient can prove
 * the file is complete and is the one that was authorized.
 */
export function ExportVault({ focusRequestId = null }: { focusRequestId?: string | null } = {}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [selectedTables, setSelectedTables] = useState<string[] | null>(null)
  const [approverEmail, setApproverEmail] = useState('')
  const [includeAttachments, setIncludeAttachments] = useState(false)
  const [tokens, setTokens] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)

  const list = useQuery({
    queryKey: ['db-console', 'export-requests'],
    queryFn: dbConsoleService.listExportRequests,
    // The second admin must see a new request, and the requester the decision,
    // without reloading. React Query pauses interval polling while the window
    // is unfocused; a request raised while the approver's window sits behind
    // another one is exactly the case that must not stall, so it keeps going.
    // The panel unmounts when another tab is chosen, which bounds the polling.
    refetchInterval: REQUEST_POLL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  })

  const exportable = list.data?.exportableTables ?? []
  const tables = selectedTables ?? exportable
  const allTablesSelected =
    exportable.length > 0 && exportable.every((table) => tables.includes(table))
  const viewer = list.data?.viewerEmail ?? ''
  // An older API sends no capability flags: offer the controls and let the
  // API answer, as before.
  const canRequest = list.data?.viewerCanRequest !== false
  const canApprove = list.data?.viewerCanApprove !== false
  const awaiting = list.data?.awaitingViewer ?? 0
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['db-console', 'export-requests'] })

  const create = useMutation({
    mutationFn: () =>
      dbConsoleService.createExportRequest({ reason: reason.trim(), tables, approverEmail, includeAttachments }),
    onSuccess: () => {
      setReason('')
      setIncludeAttachments(false)
      setBanner({
        tone: 'ok',
        text: 'Request raised. The person you named has been notified. It grants nothing until they sign off.',
      })
      void invalidate()
    },
    onError: (error) =>
      setBanner({ tone: 'warn', text: errorDetail(error) || 'The request could not be raised.' }),
  })

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      approve
        ? dbConsoleService.approveExportRequest(id, notes[id] ?? '')
        : dbConsoleService.denyExportRequest(id, notes[id] ?? ''),
    onSuccess: (row) => {
      if (row.downloadToken) {
        setTokens((prev) => ({ ...prev, [row.id]: row.downloadToken as string }))
        setBanner({
          tone: 'ok',
          text: 'Approved. The download token is shown once below and expires — it is not recoverable afterwards.',
        })
      }
      void invalidate()
    },
    onError: (error) => {
      setBanner({ tone: 'warn', text: errorDetail(error) || 'The decision could not be recorded.' })
      void invalidate()
    },
  })

  const cancel = useMutation({
    mutationFn: (id: string) => dbConsoleService.cancelExportRequest(id),
    onSuccess: () => {
      setBanner({ tone: 'ok', text: 'Request withdrawn. It can no longer be approved or downloaded.' })
      void invalidate()
    },
    // A withdrawal that failed used to fail silently, leaving the request
    // looking pending to the person who meant to pull it.
    onError: (error) => {
      setBanner({ tone: 'warn', text: errorDetail(error) || 'The request could not be withdrawn.' })
      void invalidate()
    },
  })

  const download = useMutation({
    mutationFn: ({ id, token }: { id: string; token: string }) =>
      dbConsoleService.downloadExport(id, token),
    onSuccess: (outcome, variables) => {
      downloadBlob(outcome.blob, outcome.filename)
      setTokens((prev) => {
        const next = { ...prev }
        delete next[variables.id]
        return next
      })
      setBanner({
        tone: outcome.complete ? 'ok' : 'warn',
        text: outcome.complete
          ? `Delivered ${count(outcome.rows)} rows. MANIFEST.json inside the archive lists a SHA-256 per file and the row count found in the database.`
          : `Delivered ${count(outcome.rows)} rows, but at least one table was capped — see MANIFEST.json for exactly what was omitted.`,
      })
      void invalidate()
    },
    onError: async (error) => {
      // The download is requested as a blob, so a refusal's JSON body arrives
      // as a Blob too; read it, or the reason (e.g. who may download) is lost.
      let detail = errorDetail(error)
      const data = (error as { response?: { data?: unknown } })?.response?.data
      if (!detail && data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text()) as { detail?: unknown }
          detail = typeof parsed.detail === 'string' ? parsed.detail : undefined
        } catch {
          detail = undefined
        }
      }
      setBanner({ tone: 'warn', text: detail || 'The download failed.' })
      void invalidate()
    },
  })

  return (
    <div className="flex min-h-0 flex-1 gap-2">
      <section
        aria-label="Raise an export request"
        className="flex w-[320px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-amber-300/10 bg-[#0b0b0b]"
      >
        <div className="flex items-center gap-1.5 border-b border-amber-300/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
          <ShieldCheck size={11} /> Request an export
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <p className="mb-2 text-[10px] leading-relaxed text-stone-500">
            Two-person control. You state why; someone else approves. Approval issues a single-use
            link that expires after {list.data?.tokenTtlMinutes ?? 30} minutes.
          </p>

          <label className="mb-1 block text-[9px] uppercase tracking-[0.14em] text-stone-600">
            Reason (recorded permanently)
          </label>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            placeholder="e.g. Client data-assurance review, 8 Aug"
            aria-label="Reason for the export"
            className="mb-2 w-full resize-none rounded border border-amber-300/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
          />

          <ApproverPicker value={approverEmail} onChange={setApproverEmail} />

          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">
              Tables ({tables.length}/{exportable.length})
            </span>
            <button
              type="button"
              // One control both ways: with every table ticked it clears them,
              // so picking two tables doesn't mean unticking seventeen.
              onClick={() => setSelectedTables(allTablesSelected ? [] : null)}
              className="text-[9px] text-stone-500 transition hover:text-amber-200"
            >
              {allTablesSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <ul className="mb-2 max-h-52 space-y-0.5 overflow-y-auto rounded border border-amber-300/[0.07] p-1">
            {exportable.map((table) => {
              const checked = tables.includes(table)
              return (
                <li key={table}>
                  <label className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 text-[10px] text-stone-300 hover:bg-white/[0.02]">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setSelectedTables(
                          checked ? tables.filter((t) => t !== table) : [...tables, table],
                        )
                      }
                      className="h-3 w-3 accent-amber-400"
                    />
                    <span className="truncate font-mono">{table}</span>
                  </label>
                </li>
              )
            })}
          </ul>

          <AttachmentsOption
            checked={includeAttachments}
            onChange={setIncludeAttachments}
            limitBytes={list.data?.attachmentLimitBytes}
            notice={list.data?.attachmentsNotice}
          />

          {!canRequest && (
            <p className="mb-2 flex items-start gap-1.5 rounded border border-amber-500/25 bg-amber-500/[0.06] px-2 py-1.5 text-[10px] text-amber-200">
              <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
              Your role cannot raise export requests.
            </p>
          )}
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={
              !canRequest || reason.trim().length < 8 || tables.length === 0 || !approverEmail || create.isPending
            }
            title={
              reason.trim().length < 8
                ? 'A reason of at least 8 characters is required'
                : !approverEmail
                  ? 'Choose who approves this export'
                  : undefined
            }
            className="flex w-full items-center justify-center gap-1.5 rounded border border-amber-300/25 bg-amber-400/[0.1] px-2 py-1.5 text-[11px] font-medium text-amber-100 transition hover:bg-amber-400/[0.16] disabled:opacity-40"
          >
            {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <UserCheck size={11} />}
            Raise request
          </button>

          <p className="mt-2 text-[9px] leading-relaxed text-stone-600">
            Credential tables are never exportable. Columns holding secrets are redacted, and the
            manifest lists exactly which. Each table is capped at{' '}
            {count(list.data?.rowsPerTableCap ?? 0)} rows; anything beyond that is declared in the
            manifest rather than silently dropped.
          </p>
        </div>
      </section>

      <section aria-label="Export requests" className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2 rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-2.5 py-1.5 text-[10px]">
          {awaiting > 0 ? (
            <span
              role="status"
              className="flex items-center gap-1.5 rounded border border-amber-400/40 bg-amber-400/[0.12] px-2 py-0.5 font-medium text-amber-100"
            >
              <BellRing size={11} />
              {count(awaiting)} request{awaiting === 1 ? '' : 's'} awaiting your approval
            </span>
          ) : (
            <span className="text-stone-500">
              {canApprove ? 'Nothing is waiting on your approval.' : 'You do not hold approval authority.'}
            </span>
          )}
          <span className="ml-auto flex items-center gap-1 text-stone-600">
            <RefreshCw size={9} className={clsx(list.isFetching && 'animate-spin')} />
            {list.dataUpdatedAt
              ? `Checked ${new Date(list.dataUpdatedAt).toLocaleTimeString()} · refreshes every ${REQUEST_POLL_MS / 1000} s`
              : 'Checking…'}
          </span>
        </div>

        {list.isError && (
          <p role="alert" className="flex-shrink-0 rounded border border-red-500/25 bg-red-500/[0.06] px-2.5 py-1.5 text-[11px] text-red-200">
            The export requests could not be read. {errorDetail(list.error) ?? ''}
          </p>
        )}

        {banner && (
          <p
            role="status"
            className={clsx(
              'flex flex-shrink-0 items-start gap-1.5 rounded border px-2.5 py-1.5 text-[11px]',
              banner.tone === 'ok'
                ? 'border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-200'
                : 'border-amber-500/25 bg-amber-500/[0.06] text-amber-200',
            )}
          >
            {banner.tone === 'ok' ? (
              <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            )}
            {banner.text}
          </p>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-amber-300/10 bg-[#0c0c0c]">
          {list.isLoading ? (
            <p className="p-4 text-[11px] text-stone-500">Loading requests…</p>
          ) : (list.data?.requests ?? []).length === 0 ? (
            <p className="p-4 text-[11px] text-stone-500">
              No export has ever been requested. Every request, decision and download is recorded
              here permanently.
            </p>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {(list.data?.requests ?? []).map((request) => (
                <RequestRow
                  key={request.id}
                  focused={request.id === focusRequestId}
                  request={request}
                  viewer={viewer}
                  canApprove={canApprove}
                  token={tokens[request.id]}
                  note={notes[request.id] ?? ''}
                  busy={decide.isPending || download.isPending || cancel.isPending}
                  onNote={(value) => setNotes((prev) => ({ ...prev, [request.id]: value }))}
                  onDecide={(approve) => decide.mutate({ id: request.id, approve })}
                  onCancel={() => cancel.mutate(request.id)}
                  onDownload={(token) => download.mutate({ id: request.id, token })}
                />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  )
}

function RequestRow({
  focused = false,
  request,
  viewer,
  canApprove,
  token,
  note,
  busy,
  onNote,
  onDecide,
  onCancel,
  onDownload,
}: {
  /** Opened from a link to this request (the bell, T-42): scrolled to and marked. */
  focused?: boolean
  request: ExportRequest
  viewer: string
  canApprove: boolean
  token: string | undefined
  note: string
  busy: boolean
  onNote: (value: string) => void
  onDecide: (approve: boolean) => void
  onCancel: () => void
  onDownload: (token: string) => void
}) {
  const [manualToken, setManualToken] = useState('')
  const isRequester = viewer.toLowerCase() === request.requestedByEmail.toLowerCase()
  const isApprover = viewer.toLowerCase() === (request.approvedByEmail ?? '').toLowerCase()
  const effectiveToken = token || manualToken
  const named = (request.designatedApproverEmail ?? '').toLowerCase()
  const isNamedApprover = !named || named === viewer.toLowerCase()
  const awaitingMe = request.status === 'pending' && !isRequester && canApprove && isNamedApprover
  const rowRef = useRef<HTMLLIElement>(null)
  useEffect(() => {
    if (focused) rowRef.current?.scrollIntoView?.({ block: 'center' })
  }, [focused])

  return (
    <li
      ref={rowRef}
      className={clsx(
        'px-3 py-2.5',
        awaitingMe && 'bg-amber-400/[0.05] ring-1 ring-inset ring-amber-400/30',
        focused && 'ring-2 ring-inset ring-amber-300/70',
      )}
      data-awaiting-viewer={awaitingMe ? 'true' : undefined}
      data-focused-request={focused ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className={clsx(
            'rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ring-1 ring-inset',
            STATUS_STYLE[request.status] ?? STATUS_STYLE.cancelled,
          )}
        >
          {request.status}
        </span>
        <span className="font-mono text-[10px] text-stone-500">{request.id}</span>
        <span className="text-[11px] text-stone-200">{request.reason}</span>
        <span className="ml-auto text-[9px] text-stone-600">{when(request.createdAt)}</span>
      </div>

      <div className="mt-1 grid gap-x-5 gap-y-0.5 text-[10px] text-stone-500 md:grid-cols-2 xl:grid-cols-4">
        <span>
          Requested by <span className="text-stone-300">{request.requestedByEmail}</span>
        </span>
        <span>
          Decision: <span className="text-stone-300">{decisionLine(request)}</span>
        </span>
        <span title={request.scope.join(', ')}>
          {request.scope.length === 1 ? `Table ${request.scope[0]}` : `${request.scope.length} tables`}
        </span>
        {request.includeAttachments && <span>With the attachment files</span>}
        {request.downloadedAt && (
          <span>
            Downloaded {when(request.downloadedAt)} · {formatBytes(request.byteSize)}
          </span>
        )}
      </div>
      {request.decisionNote && (
        <p className="mt-0.5 text-[10px] text-stone-500">
          Note: <span className="text-stone-300">{request.decisionNote}</span>
        </p>
      )}

      {request.status === 'pending' && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {isRequester ? (
            <>
              <span className="flex items-center gap-1 rounded border border-amber-500/25 bg-amber-500/[0.06] px-2 py-1 text-[10px] text-amber-200">
                <AlertTriangle size={10} />
                You raised this — a different person must approve it
              </span>
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="rounded border border-amber-300/15 px-2 py-1 text-[10px] text-stone-300 transition hover:border-amber-300/30 disabled:opacity-40"
              >
                Withdraw
              </button>
            </>
          ) : !canApprove || !isNamedApprover ? (
            <span className="text-[10px] text-stone-500">
              {isNamedApprover
                ? 'Awaiting an admin with approval authority.'
                : `Waiting for ${request.designatedApproverName || request.designatedApproverEmail} to decide. Only they can.`}
            </span>
          ) : (
            <>
              <span className="flex items-center gap-1 text-[10px] font-medium text-amber-200">
                <BellRing size={10} /> Awaiting your decision
              </span>
              <input
                value={note}
                onChange={(event) => onNote(event.target.value)}
                placeholder="Decision note (recorded)"
                aria-label={`Decision note for ${request.id}`}
                className="min-w-0 flex-1 rounded border border-amber-300/10 bg-black/40 px-2 py-1 text-[10px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => onDecide(true)}
                disabled={busy}
                className="flex items-center gap-1 rounded border border-emerald-400/25 bg-emerald-400/[0.08] px-2 py-1 text-[10px] text-emerald-200 transition hover:bg-emerald-400/[0.14] disabled:opacity-40"
              >
                <CheckCircle2 size={10} /> Approve
              </button>
              <button
                type="button"
                onClick={() => onDecide(false)}
                disabled={busy}
                className="flex items-center gap-1 rounded border border-red-400/25 bg-red-400/[0.08] px-2 py-1 text-[10px] text-red-200 transition hover:bg-red-400/[0.14] disabled:opacity-40"
              >
                <XCircle size={10} /> Deny
              </button>
            </>
          )}
        </div>
      )}

      {request.status === 'approved' && !isRequester && !isApprover && (
        <p className="mt-1.5 text-[10px] text-stone-500">
          Only {request.requestedByEmail} or {request.approvedByEmail ?? 'the approver'} can download this export.
        </p>
      )}

      {request.status === 'approved' && (isRequester || isApprover) && (
        <div className="mt-1.5">
          {token ? (
            <p className="mb-1 text-[10px] text-sky-200">
              Give this token to {request.requestedByEmail}, or download here. It is shown once and
              cannot be recovered.
            </p>
          ) : (
            <p className="mb-1 text-[10px] text-stone-400">
              {isRequester
                ? `Approved by ${request.approvedByEmail ?? 'a second admin'}. Paste the one-time token they received at approval.`
                : 'Paste the one-time token shown to you at approval.'}
            </p>
          )}
          {token ? (
            <div className="mb-1.5 flex items-center gap-1.5 rounded border border-sky-500/25 bg-sky-500/[0.06] px-2 py-1">
              <span className="text-[9px] uppercase tracking-wider text-sky-300">
                One-time token
              </span>
              <code className="min-w-0 flex-1 truncate font-mono text-[10px] text-stone-200">
                {token}
              </code>
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(token)}
                aria-label="Copy download token"
                className="rounded p-1 text-stone-400 transition hover:text-amber-200"
              >
                <Copy size={10} />
              </button>
            </div>
          ) : (
            <input
              value={manualToken}
              onChange={(event) => setManualToken(event.target.value)}
              placeholder="Paste the one-time token issued at approval"
              aria-label="Download token"
              className="mb-1.5 w-full rounded border border-amber-300/10 bg-black/40 px-2 py-1 text-[10px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
            />
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => onDownload(effectiveToken)}
              disabled={busy || !effectiveToken || request.tokenExpired}
              title={request.tokenExpired ? 'The download window has closed' : undefined}
              className="flex items-center gap-1 rounded border border-amber-300/25 bg-amber-400/[0.1] px-2 py-1 text-[10px] text-amber-100 transition hover:bg-amber-400/[0.16] disabled:opacity-40"
            >
              {busy ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
              Download once
            </button>
            <span
              className={clsx(
                'text-[9px]',
                request.tokenExpired ? 'text-red-300' : 'text-stone-600',
              )}
            >
              {request.tokenExpired
                ? 'Window closed — raise a new request'
                : `Expires ${when(request.tokenExpiresAt)}`}
            </span>
          </div>
        </div>
      )}

      {request.manifest && (
        <details className="mt-1.5">
          <summary className="cursor-pointer text-[10px] text-stone-500 transition hover:text-amber-200">
            Delivery manifest — {request.manifest.complete ? 'complete' : 'partial'},{' '}
            {request.manifest.files.length} files with checksums
          </summary>
          <table className="mt-1 w-full text-left text-[10px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-stone-600">
                <th scope="col" className="py-0.5 pr-3 font-medium">Table</th>
                <th scope="col" className="py-0.5 pr-3 text-right font-medium">In database</th>
                <th scope="col" className="py-0.5 pr-3 text-right font-medium">Written</th>
                <th scope="col" className="py-0.5 pr-3 font-medium">SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {request.manifest.files.map((file) => (
                <tr key={file.table} className="border-t border-white/[0.04]">
                  <td className="py-0.5 pr-3 font-mono text-stone-300">{file.table}</td>
                  <td className="py-0.5 pr-3 text-right font-mono text-stone-400">
                    {file.rowsInDatabase === null ? '—' : count(file.rowsInDatabase)}
                  </td>
                  <td
                    className={clsx(
                      'py-0.5 pr-3 text-right font-mono',
                      file.complete === false ? 'text-amber-300' : 'text-stone-200',
                    )}
                  >
                    {count(file.rowsWritten)}
                  </td>
                  <td
                    className="max-w-[200px] truncate py-0.5 pr-3 font-mono text-stone-600"
                    title={file.sha256}
                  >
                    {file.sha256 ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </li>
  )
}
